import { makeAutoObservable, runInAction } from 'mobx';
import { indexSymbols } from '@app/common';
import type { RootStore } from '../root-store';
import type { StatisticStore } from './statistic-store';
import { formatApiDate } from '../../functions/format-api-date';
import { TabEnum } from '../../enums/tab-enum';
import { AreaChartMobxDto } from '../../mobx/dtos/statistic/area-chart-mobx-dto';
import { ChartDataEnum, StatisticControllerGetProfitChartsRequest } from '../../../defs/api';
import { getBaseSeriesId } from '../../constants/return-metric';

export class ProfitStore {
    rootStore: RootStore;

    statisticStore: StatisticStore;

    percentageProfits: AreaChartMobxDto[] = [];

    otherProfits: AreaChartMobxDto[] = [];

    absoluteProfits: AreaChartMobxDto[] = [];

    filter: Map<string, boolean> = new Map<string, boolean>();

    tab = TabEnum.Absolute;

    showChart: boolean = false;

    loading = true;

    constructor(statisticStore: StatisticStore) {
        this.statisticStore = statisticStore;
        this.rootStore = statisticStore.rootStore;

        makeAutoObservable(this);
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    setShowChart(fetched: boolean = true) {
        this.showChart = fetched;
    }

    async fetchProfitCharts(filterChart = true): Promise<void> {
        const portfolioId = this.rootStore.portfolioStore.selectedPortfolio.id;
        const interval = this.statisticStore.intervalType;

        const parameters: StatisticControllerGetProfitChartsRequest = {
            portfolioId,
            interval,
        };

        if (this.statisticStore.dateFrom) {
            parameters.dateFrom = formatApiDate(this.statisticStore.dateFrom);
        }

        this.startLoading();

        try {
            const fetchedAbsoluteProfit = await this.rootStore.loadWithDelay(() => {
                const isSharedPortfolio = this.rootStore.portfolioStore.sharedPortfolio !== null;
                return isSharedPortfolio
                    ? this.rootStore.apiClient.statisticsController.sharedStatisticControllerGetProfitCharts(parameters)
                    : this.rootStore.apiClient.statisticsController.statisticControllerGetProfitCharts(parameters);
            });
            if (fetchedAbsoluteProfit) {
                runInAction(() => {
                    this.absoluteProfits = AreaChartMobxDto.createFromArray(fetchedAbsoluteProfit.absoluteProfits);
                    this.percentageProfits = AreaChartMobxDto.createFromArray(fetchedAbsoluteProfit.percentageProfits);
                    this.otherProfits = AreaChartMobxDto.createFromArray(fetchedAbsoluteProfit.otherProfits);

                    if (filterChart) {
                        if (!this.filter.size) {
                            this.filterSelected();
                        }
                    }
                });
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }

    setTab(tab: TabEnum) {
        this.tab = tab;
    }

    filterSelected(id?: string): void {
        if (id) {
            const filterKey = this.tab === TabEnum.Percentage ? id : getBaseSeriesId(id);
            const filterValue = this.filter.get(filterKey);
            runInAction(() => {
                this.filter.set(filterKey, !filterValue);
            });
        } else {
            const symbols = indexSymbols.map((s) => s.symbol);

            this.filter.clear();

            const applyDefaultFilter = (chartList: AreaChartMobxDto[]) => {
                chartList.forEach((chart) => {
                    const baseId = getBaseSeriesId(chart.id);
                    const filterKey = this.tab === TabEnum.Percentage ? chart.id : baseId;
                    const isIndexChart = symbols.includes(baseId);
                    const isPinned = chart.chartDataType === ChartDataEnum.PinnedPortfolioValue;

                    const wasSet = this.filter.has(filterKey);

                    if (wasSet && isPinned) {
                        return;
                    }

                    this.filter.set(filterKey, !isIndexChart && !isPinned);
                });
            };

            if (this.tab === TabEnum.Absolute) {
                applyDefaultFilter(this.absoluteProfits);
            } else if (this.tab === TabEnum.Percentage) {
                applyDefaultFilter(this.percentageProfits);
            }
        }

        runInAction(() => {
            this.setShowChart(true);
        });
    }

    syncVisibleChartFilter(chartList: AreaChartMobxDto[]): void {
        const symbols = indexSymbols.map((s) => s.symbol);

        chartList.forEach((chart) => {
            const baseId = getBaseSeriesId(chart.id);
            const filterKey = this.tab === TabEnum.Percentage ? chart.id : baseId;

            if (this.filter.has(filterKey)) {
                return;
            }

            const isIndexChart = symbols.includes(baseId);
            const isPinned = chart.chartDataType === ChartDataEnum.PinnedPortfolioValue;

            this.filter.set(filterKey, !isIndexChart && !isPinned);
        });
    }
}
